import manifest from '@shared/permissions.json'

/** @type {Record<string, string>} */
export const PERMISSIONS = Object.fromEntries(
  manifest.permissions.map((permission) => [permission.key, permission.slug]),
)

/** @type {typeof manifest.permissions} */
export const PERMISSION_DEFINITIONS = manifest.permissions

/** @type {Record<string, string[]>} */
const LEGACY_FALLBACKS = {
  [PERMISSIONS.MENU_APPOINTMENTS]: [PERMISSIONS.APPOINTMENTS_MANAGE],
  [PERMISSIONS.MENU_ONLINE_BOOKINGS]: [PERMISSIONS.APPOINTMENTS_MANAGE],
  [PERMISSIONS.MENU_CHECKOUT]: [PERMISSIONS.POS_ACCESS],
  [PERMISSIONS.MENU_CUSTOMERS]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_SERVICES]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_STAFF]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_SALES]: [PERMISSIONS.REPORTS_VIEW],
  [PERMISSIONS.MENU_CASH]: [PERMISSIONS.ACCOUNTS_ACCESS],
  [PERMISSIONS.MENU_PRODUCTS]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_PACKAGES]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_REPORTS]: [PERMISSIONS.REPORTS_VIEW],
  [PERMISSIONS.MENU_LEADS]: [PERMISSIONS.CUSTOMERS_VIEW],
  [PERMISSIONS.MENU_HR]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_APPROVALS]: [PERMISSIONS.ADMIN_FULL],
  [PERMISSIONS.MENU_ACCOUNTS]: [PERMISSIONS.ACCOUNTS_ACCESS],
  [PERMISSIONS.MENU_SEND_OFFERS]: [PERMISSIONS.ADMIN_FULL],
  [PERMISSIONS.MENU_SETTINGS]: [PERMISSIONS.ADMIN_FULL],
  [PERMISSIONS.MENU_REPORTS]: [PERMISSIONS.REPORTS_VIEW],
  [PERMISSIONS.MENU_LEADS]: [PERMISSIONS.CUSTOMERS_VIEW, PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.MENU_HR]: [PERMISSIONS.MASTERS_MANAGE, PERMISSIONS.HR_LEAVE, PERMISSIONS.HR_ATTENDANCE],
  [PERMISSIONS.MENU_APPROVALS]: [PERMISSIONS.MASTERS_MANAGE, PERMISSIONS.ADMIN_FULL],
  [PERMISSIONS.MENU_ACCOUNTS]: [PERMISSIONS.ACCOUNTS_ACCESS],
  [PERMISSIONS.HR_LEAVE]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.HR_ATTENDANCE]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.CUSTOMERS_VIEW]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.CUSTOMERS_CREATE]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.CUSTOMERS_EDIT]: [PERMISSIONS.MASTERS_MANAGE],
  [PERMISSIONS.CUSTOMERS_DELETE]: [PERMISSIONS.MASTERS_MANAGE],
}

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string} permission
 */
export function hasPermission(user, permission) {
  const permissions = user?.permissions ?? []
  if (permissions.includes(PERMISSIONS.ADMIN_FULL)) {
    return true
  }
  return permissions.includes(permission)
}

/**
 * Includes legacy coarse permissions (e.g. masters.manage → customers.edit).
 *
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string} permission
 */
export function hasEffectivePermission(user, permission) {
  if (hasPermission(user, permission)) {
    return true
  }

  return (LEGACY_FALLBACKS[permission] ?? []).some((legacy) => hasPermission(user, legacy))
}

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string} permissionsExpression pipe-separated OR list
 */
export function hasAnyEffectivePermission(user, permissionsExpression) {
  return permissionsExpression
    .split('|')
    .map((slug) => slug.trim())
    .filter(Boolean)
    .some((slug) => hasEffectivePermission(user, slug))
}

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string[]} requiredPermissions
 */
export function hasAnyPermission(user, requiredPermissions) {
  return requiredPermissions.some((permission) => hasEffectivePermission(user, permission))
}
